"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, FileText, ChartBar as BarChart3, Compass, Briefcase, Layers, ArrowRight, Sparkles, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface ResourceFormat {
  id: string;
  name: string;
  count: string;
  description: string;
  link: string;
  iconName: string;
}
const formats: ResourceFormat[] = [{
  id: "research-reports",
  name: "Research reports",
  count: "40+ reports",
  description: "Peer-reviewed benchmark studies and quantitative sector analyses.",
  link: "/ResearchReportsPage",
  iconName: "BarChart3"
}, {
  id: "ebooks-guides",
  name: "Ebooks & strategic guides",
  count: "28 volumes",
  description: "In-depth playbooks and frameworks for senior enterprise decision-makers.",
  link: "/EbooksGuidesPage",
  iconName: "BookOpen"
}, {
  id: "industry-insights",
  name: "Industry insights",
  count: "120+ briefs",
  description: "Regular executive commentaries, market perspectives, and trend breakdowns.",
  link: "/IndustryInsightsPage",
  iconName: "Compass"
}, {
  id: "case-studies",
  name: "Executive case studies",
  count: "35 studies",
  description: "Verified organizational case analyses across global B2B operations.",
  link: "/CaseStudiesPage",
  iconName: "Briefcase"
}];
const pillars = [{
  title: "Zero sponsored bias",
  detail: "Editorial independence across all methodologies and published indices."
}, {
  title: "Open executive access",
  detail: "Directly preview key findings and summaries before requesting downloads."
}, {
  title: "Privacy compliant",
  detail: "Strict adherence to GDPR standards with zero third-party lead selling."
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const getFormatIcon = (name: string) => {
    switch (name) {
      case "BarChart3":
        return <BarChart3 className="size-4 text-primary" />;
      case "BookOpen":
        return <BookOpen className="size-4 text-primary" />;
      case "Compass":
        return <Compass className="size-4 text-primary" />;
      case "Briefcase":
        return <Briefcase className="size-4 text-primary" />;
      default:
        return <FileText className="size-4 text-primary" />;
    }
  };
  const transitionConfig = {
    duration: shouldReduceMotion ? 0.01 : 0.45,
    ease: [0.16, 1, 0.3, 1] as const
  };
  return <section id="library-overview" data-nav="light" className="relative bg-background text-foreground pt-12 pb-16 md:pt-16 md:pb-20 border-b border-border">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Masthead rule design motif */}
        <div className="flex flex-col gap-1 mb-8" aria-hidden="true">
          <div className="w-full h-px bg-primary/80" />
          <div className="w-full h-px bg-border" />
        </div>

        {/* Editorial Top Eyebrow & Meta Indicator */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-chart-2" />
            <span className="font-sans text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              MinfoMedia Knowledge Repository
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-card text-muted-foreground border-border font-mono text-[11px] rounded-sm py-0.5 px-2.5">
              Updated Weekly
            </Badge>
            <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-mono text-[11px] rounded-sm py-0.5 px-2.5">
              Peer-Reviewed
            </Badge>
          </div>
        </div>

        {/* Header Content & Editorial Lead */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={transitionConfig} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-7">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
              The research and analysis library
            </h1>
            <p className="font-sans text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Browse verified industry reports, strategic executive guides, and market intelligence publications designed for B2B leaders. Read transparent summaries and analytical frameworks before choosing what to download.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ResearchReportsPage">
                  <span>Explore research reports</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-sm border-border hover:bg-muted">
                <Link to="/EbooksGuidesPage">
                  <span>View all guides</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Institutional Trust Sidebar */}
          <div className="lg:col-span-5 bg-card border border-border rounded-md p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
              <ShieldCheck className="size-4 text-chart-2" />
              <h2 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                Editorial & Access Standards
              </h2>
            </div>

            <div className="space-y-4">
              {pillars.map((pillar, idx) => <div key={pillar.title} data-index={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-sans text-sm font-semibold text-foreground">
                      {pillar.title}
                    </h3>
                    <p className="font-sans text-xs text-muted-foreground mt-0.5 leading-normal">
                      {pillar.detail}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <span>ISSN 2940-1182 Institutional Catalog</span>
              <Link to="/AboutUsPage" className="text-foreground hover:underline font-medium">
                Our methodology
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Format Breakdown Section Header */}
        <div className="mt-14 pt-8 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                Browse publication formats
              </h2>
              <p className="font-sans text-sm text-muted-foreground mt-1">
                Select a collection to filter by methodology, medium, and scope.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <Layers className="size-4" />
              <span>4 Primary Formats</span>
            </div>
          </div>

          {/* Format Breakdown Indicator Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {formats.map((fmt, idx) => <div key={fmt.id} data-index={idx}>
                <Card className="h-full rounded-md border border-border bg-card p-5 transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-xs group">
                  <CardContent className="p-0 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="p-2 rounded-sm bg-muted inline-flex items-center justify-center">
                          {getFormatIcon(fmt.iconName)}
                        </div>
                        <Badge variant="outline" className="font-mono text-[10px] text-muted-foreground border-border rounded-sm py-0.5 px-1.5">
                          {fmt.count}
                        </Badge>
                      </div>

                      <h3 className="font-serif text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                        {fmt.name}
                      </h3>
                      <p className="font-sans text-xs text-muted-foreground mt-2 leading-relaxed">
                        {fmt.description}
                      </p>
                    </div>

                    <div className="mt-5 pt-3 border-t border-border/60">
                      <Link to={fmt.link} className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-[color,transform]">
                        <span>Browse directory</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Quick Topics Pill Strip */}
        <div className="mt-8 flex flex-wrap items-center gap-2 bg-muted/50 p-3.5 rounded-md border border-border">
          <span className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-2">
            Key research tracks:
          </span>
          {["Enterprise Tech", "Revenue Operations", "Supply Chain", "Generative AI", "B2B Marketing"].map((topic, i) => <span key={topic} data-index={i} className="inline-flex items-center rounded-full bg-card px-3 py-1 text-xs font-medium text-foreground border border-border/80 shadow-2xs">
              <Sparkles className="size-3 mr-1.5 text-chart-2" />
              {topic}
            </span>)}
        </div>
      </div>
    </section>;
}